import type {PropagatorType, TracingOption} from '@datadog/browser-rum';

import config from '../../config';
import {getDatadogMode} from './datadogMode';

export type GlobalViewer = {
  id: string;
  username?: string;
  entity: string;
  org?: string;
};

declare global {
  interface Window {
    // This matches what's in app/src/util/window.ts
    viewer: GlobalViewer | undefined;
  }
}

// A string entry matches every request URL that starts with it (browser-rum-core
// tracer.ts, matchList with useStartsWith), so an origin covers all its paths.
//
// Under the Forge shell the app calls gorilla, weave and trace same-origin on
// the shell host (/api/wandb, /api/weave, /api/trace; REACT_APP_SHELL_*_HOST in
// environments/*.env), and the platform load balancer forwards those to the
// api./weave./trace. hosts below. Listing the shell origin is what gets the
// b3 headers, and so @_dd.trace_id, onto those resource events.
const STATIC_TRACING_URLS = [
  // local dev
  'http://api.wandb.test',
  'https://api.wandb.test',
  'https://weave.wandb.test',
  'https://trace.wandb.test',
  'https://trace-server.wandb.test',
  'https://forge.wandb.test',

  // qa
  'https://api.qa.wandb.ai',
  'https://weave.qa.wandb.ai',
  'https://trace.qa.wandb.ai',
  'https://weave-trace.qa.wandb.ai',
  'https://qa.forge.coreweave.com',

  // prod
  'https://api.wandb.ai',
  'https://weave.wandb.ai',
  'https://trace.wandb.ai',
  'https://forge.coreweave.com',
];

const B3MULTI: PropagatorType = 'b3multi';

function buildAllowedTracingUrls(): TracingOption[] {
  const isServerRum = getDatadogMode() === 'server';
  const urls = [...STATIC_TRACING_URLS];
  const baseUrl = isServerRum ? window.location.origin : undefined;

  // An empty API host means the backend is co-hosted with the app.
  if (config.BACKEND_HOST) {
    if (isServerRum && config.BACKEND_HOST.startsWith('/')) {
      try {
        urls.push(new URL(config.BACKEND_HOST, baseUrl).href);
      } catch {
        // Ignore malformed backend URLs.
      }
    } else {
      urls.push(config.BACKEND_HOST);
    }
  } else if (isServerRum) {
    urls.push(window.location.origin);
  }

  // Weave includes a service path; tracing only needs its origin.
  if (config.WEAVE_BACKEND_HOST) {
    try {
      urls.push(new URL(config.WEAVE_BACKEND_HOST, baseUrl).origin);
    } catch {
      // Ignore malformed URLs.
    }
  }

  if (
    config.TRACE_BACKEND_BASE_URL &&
    (isServerRum || config.TRACE_BACKEND_BASE_URL.startsWith('http'))
  ) {
    try {
      urls.push(new URL(config.TRACE_BACKEND_BASE_URL, baseUrl).origin);
    } catch {
      // Ignore malformed URLs.
    }
  }

  return [...new Set(urls)].map(url => ({
    match: url,
    propagatorTypes: [B3MULTI],
  }));
}

export const ALLOWED_TRACING_URLS: readonly TracingOption[] =
  buildAllowedTracingUrls();

export const DATADOG_RUM_APPLICATION_ID =
  '70585d24-006d-424f-a3c5-fbe0403f9ebf';
export const DATADOG_CLIENT_TOKEN = 'pub44b08232e19f1a1651a49e62607b9fcd'; //gitleaks:allow
export const DATADOG_SITE = 'us5.datadoghq.com';
export const DATADOG_UI_SERVICE = 'wandb-web-ui';
